import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, type BuildEvent, type Job } from "../../lib/api";
import { streamBuildEvents } from "../../lib/sse";
import { useAppDialog } from "../../components/AppDialog";

import { BuildWaiting } from "./BuildWaiting";
import { buildFailure } from "./buildFailure";
import { currentBuildStatus } from "./buildProgress";

export function statusLabel(s: string): string {
  switch (s) {
    case "queued":     return "Getting started";
    case "grounding": return "Getting to know your business";
    case "extracting_inspo":
    case "synthesizing":
    case "scaffolding": return "Exploring the design";
    case "building":   return "Building your site";
    case "validating": return "Final checks";
    case "shipping":
    case "publishing": return "Publishing preview";
    case "delivery_pending": return "Connecting your preview";
    case "ready":      return "Ready to preview";
    case "ready_unverified": return "Preview needs verification";
    case "retrying": return "Recovering your build";
    case "runner_start_failed": return "Could not start";
    case "timeout": return "Build timed out";
    case "failed":     return "Build failed";
    case "cancelled":  return "Build cancelled";
    default:           return s;
  }
}

export function isWorkingStatus(status: Job["status"]): boolean {
  return [
    "queued", "grounding", "extracting_inspo", "synthesizing", "scaffolding",
    "building", "validating", "shipping", "publishing", "delivery_pending", "retrying",
  ].includes(status);
}

// Persisted opt-in for the "tell me when it's done" browser notification.
const NOTIFY_KEY = "protopal-notify-builds";

export default function SiteBuilds() {
  const { siteId, jobId } = useParams<{ siteId: string; jobId?: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [events, setEvents] = useState<BuildEvent[]>([]);
  const [showTimeline, setShowTimeline] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryErr, setRetryErr] = useState<string | null>(null);
  const dialog = useAppDialog();
  const [cancelling, setCancelling] = useState(false);
  const [cancelErr, setCancelErr] = useState<string | null>(null);
  const [statusErr, setStatusErr] = useState<string | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const notificationsSupported = typeof window !== "undefined" && "Notification" in window;
  const [notifyOn, setNotifyOn] = useState(() =>
    notificationsSupported &&
    Notification.permission === "granted" &&
    localStorage.getItem(NOTIFY_KEY) === "1");
  const [notifyDenied, setNotifyDenied] = useState(() =>
    notificationsSupported && Notification.permission === "denied");
  const prevStatus = useRef<Job["status"] | null>(null);

  async function toggleNotify() {
    if (!notificationsSupported) return;
    if (notifyOn) {
      localStorage.removeItem(NOTIFY_KEY);
      setNotifyOn(false);
      return;
    }
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      localStorage.setItem(NOTIFY_KEY, "1");
      setNotifyOn(true);
      setNotifyDenied(false);
    } else if (permission === "denied") {
      setNotifyDenied(true);
    }
  }

  // SSE for live event stream.
  useEffect(() => {
    if (!jobId) return;
    setEvents([]);
    setRetrying(false);
    setRetryErr(null);
    setCancelling(false);
    setCancelErr(null);
    setJob(null);
    setStatusErr(null);
    prevStatus.current = null;
    const teardown = streamBuildEvents(jobId, (e) => {
      setEvents((prev) => [...prev, e]);
      requestAnimationFrame(() => {
        if (timelineRef.current) timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
      });
    });
    return teardown;
  }, [jobId]);

  // Polling job status — SSE doesn't always cleanly close on terminal,
  // and we want to drive the page's overall state from the canonical
  // job doc, not from inferred event types.
  useEffect(() => {
    if (!jobId) return;
    let stopped = false;
    let timer: number;
    const tick = async () => {
      try {
        const res = await api.getJob(jobId);
        if (stopped) return;
        setJob(res.job);
        setStatusErr(null);
      } catch {
        if (stopped) return;
        setStatusErr("We’re reconnecting to your existing build. You don’t need to start again.");
      } finally {
        if (!stopped) timer = window.setTimeout(() => void tick(), 4000);
      }
    };
    void tick();
    return () => { stopped = true; window.clearTimeout(timer); };
  }, [jobId]);

  // Auto-bounce to the preview page once the build is ready. Two-
  // second pause lets the success state register visually.
  useEffect(() => {
    if (!siteId) return;
    if (currentBuildStatus(job) === "ready") {
      const t = window.setTimeout(() => navigate(`/sites/${siteId}/preview`), 2000);
      return () => window.clearTimeout(t);
    }
  }, [job?.status, job?.execution?.status, siteId, navigate]);

  // Browser notification on the working → terminal transition, so stepping
  // away from the tab is actually safe ("we'll tell you when it's done").
  // Fires only when the tab is hidden — on-screen the page itself announces.
  useEffect(() => {
    const status = currentBuildStatus(job) ?? null;
    const previous = prevStatus.current;
    prevStatus.current = status;
    if (!status || !previous || status === previous) return;
    if (!isWorkingStatus(previous) || isWorkingStatus(status)) return;
    if (!notifyOn || !notificationsSupported || Notification.permission !== "granted") return;
    if (!document.hidden) return;
    const ok = status === "ready" || status === "ready_unverified";
    try {
      const notification = new Notification(
        ok ? "Your ProtoPal site is ready" : "Your ProtoPal build stopped",
        {
          body: ok
            ? "The preview is built. Come take a look."
            : `Status: ${statusLabel(status)}. Your draft is safe.`,
          icon: "/favicon.png",
          tag: `protopal-build-${jobId ?? "current"}`,
        },
      );
      notification.onclick = () => { window.focus(); notification.close(); };
    } catch {
      // Notification constructor can throw on some mobile browsers — the
      // in-page status remains the source of truth.
    }
  }, [job?.status, job?.execution?.status, jobId, notificationsSupported, notifyOn]);

  const status = currentBuildStatus(job);
  const failure = buildFailure(job, events);
  const isWorking = status ? isWorkingStatus(status) : false;
  async function handleCancel() {
    if (!siteId || !jobId) return;
    const confirmed = await dialog.confirm({
      title: "Stop this build?",
      body: "You can start a new one whenever you're ready.",
      confirmLabel: "Stop build",
      cancelLabel: "Keep building",
      tone: "danger",
    });
    if (!confirmed) return;
    setCancelling(true);
    setCancelErr(null);
    try {
      await api.cancelBuild(siteId, jobId);
      const res = await api.getJob(jobId);
      setJob(res.job);
    } catch (err: any) {
      setCancelErr(err?.payload?.error || "The build could not be cancelled.");
    } finally {
      setCancelling(false);
    }
  }

  async function handleRetry() {
    if (!siteId) return;
    setRetrying(true);
    setRetryErr(null);
    try {
      const parent = job?.execution?.engineVersion === "studio-v2" && currentBuildStatus(job) === "failed" ? jobId : undefined;
      const r = await api.startBuild(siteId, "preview", undefined, undefined, undefined, parent);
      navigate(`/sites/${siteId}/builds/${r.jobId}`);
    } catch (err: any) {
      setRetryErr(err?.payload?.error || "Retry failed.");
      setRetrying(false);
    }
  }

  return (
    <div className="builder-page build-studio-page">
      <div className="row between" style={{ marginBottom: 12 }}>
        <Link to={`/sites/${siteId}`} className="muted" style={{ fontSize: 13, textDecoration: "none" }}>
          ← Back to site
        </Link>
        <span className={`badge ${status === "ready" ? "good" : status && ["failed", "runner_start_failed", "timeout"].includes(status) ? "bad" : status === "cancelled" ? "" : "warn"}`}>
          {status ? statusLabel(status) : "Checking status"}
        </span>
      </div>

      <div className="builder-card">
        {!status && (
          <div className="builder-status">
            <div className="builder-x" style={{ background: statusErr ? "#d97706" : "#64748b" }}>
              {statusErr ? "!" : "…"}
            </div>
            <div>
              <div className="builder-title">{statusErr ? "Build status is temporarily unavailable." : "Checking build status…"}</div>
              <div className="builder-phrase">
                {statusErr ?? "This page will update as soon as the job responds."}
              </div>
            </div>
          </div>
        )}

        {isWorking && (
          <>
            <BuildWaiting status={status!} events={events} job={job} reconnecting={!!statusErr} />
            <div className="builder-actions">
              {notificationsSupported && !notifyDenied && (
                <button onClick={() => void toggleNotify()} className={`btn${notifyOn ? " primary" : ""}`}>
                  {notifyOn ? "✓ Browser notifications on" : "Notify me in this browser"}
                </button>
              )}
              <button onClick={handleCancel} disabled={cancelling} className="btn">
                {cancelling ? "Cancelling…" : "Cancel this build"}
              </button>
            </div>
            {notifyDenied && (
              <div className="muted" style={{ marginTop: 8, textAlign: "center", fontSize: 12 }}>
                Notifications are blocked for this site — enable them in your browser settings to get a ping when the build finishes.
              </div>
            )}
            {cancelErr && <div className="muted" style={{ marginTop: 8, textAlign: "center", color: "#b91c1c", fontSize: 13 }}>{cancelErr}</div>}
          </>
        )}

        {status === "ready" && (
          <div className="builder-status">
            <div className="builder-checkmark">✓</div>
            <div>
              <div className="builder-title">Your site is ready.</div>
              <div className="builder-phrase">Opening your preview…</div>
            </div>
          </div>
        )}

        {status === "ready_unverified" && (
          <>
            <div className="builder-status">
              <div className="builder-x" style={{ background: "#d97706" }}>!</div>
              <div>
                <div className="builder-title">Your draft is built and ready to edit.</div>
                <div className="builder-phrase">The public preview check was delayed, but the uploaded draft is available.</div>
              </div>
            </div>
            <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <Link to={`/sites/${siteId}/editor`} className="btn primary">Edit site</Link>
              <Link to={`/sites/${siteId}/preview`} className="btn">Open preview</Link>
              <Link to={`/sites/${siteId}`} className="btn">Back to site</Link>
            </div>
          </>
        )}

        {(status === "failed" || status === "runner_start_failed" || status === "timeout") && (
          <>
            <div className="builder-status">
              <div className="builder-x">!</div>
              <div>
                <div className="builder-title">
                  {failure.title}
                </div>
                <div className="builder-phrase">{failure.detail}</div>
              </div>
            </div>
            {failure.retry && <button onClick={handleRetry} disabled={retrying} className="btn primary" style={{ marginTop: 12 }}>
              {retrying ? "Restarting…" : "Try again"}
            </button>}
            {!failure.retry && <Link to={`/sites/${siteId}`} className="btn" style={{ marginTop: 12 }}>{failure.kind === "input" ? "Review business details" : "Back to site"}</Link>}
            {retryErr && <div className="muted" style={{ marginTop: 8, color: "#b91c1c", fontSize: 13 }}>{retryErr}</div>}
          </>
        )}

        {status === "cancelled" && (
          <>
            <div className="builder-status">
              <div className="builder-x" style={{ background: "#9ca3af" }}>—</div>
              <div>
                <div className="builder-title">This build was cancelled.</div>
                <div className="builder-phrase">Start a fresh one when you're ready.</div>
              </div>
            </div>
            <button onClick={handleRetry} disabled={retrying} className="btn primary" style={{ marginTop: 12 }}>
              {retrying ? "Starting…" : "Start a new build"}
            </button>
            {retryErr && <div className="muted" style={{ marginTop: 8, color: "#b91c1c", fontSize: 13 }}>{retryErr}</div>}
          </>
        )}
      </div>

      {events.length > 0 && (
        <div style={{ marginTop: 14, textAlign: "center" }}>
          <button
            type="button"
            onClick={() => setShowTimeline((v) => !v)}
            style={{
              background: "none", border: "none", color: "var(--muted)",
              fontSize: 12, cursor: "pointer", padding: 0,
            }}
          >
            {showTimeline ? "Hide" : "Show"} event log
          </button>
          {showTimeline && (
            <div className="timeline" ref={timelineRef} style={{ marginTop: 8, textAlign: "left" }}>
              {events.map((e, i) => (
                <div key={i}>
                  <span className="ts">{new Date(e.ts).toLocaleTimeString()}</span>{" "}
                  <span className="role">{e.role}</span>{" "}
                  <span className={e.level === "error" ? "err" : ""}>{e.msg}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
